import { useEffect, useCallback, useState } from 'react';
import { View, StyleSheet, ScrollView, Image } from 'react-native';
import { useNavigate, useSearchParams } from 'react-router';
import { Button, Logo, theme } from '@socrait/react';
import { showMessage } from 'react-native-flash-message';

import { Loading } from '~/components/Loading';
import { LeftoversModal } from '~/components/LeftoversModal';
import { SetupProgressIndicator } from '~/components/SetupProgressIndicator';
import { trackEvent, trackConversion } from '~/gtag';
import { createLogger } from '~/utils/logging';
import { sessionStore } from '~/store/sessionStore';
import { setupStore } from '~/store/setupStore';
import { useSetupMutation } from '~/services/useSetupMutation';
import { useLogoutMutation } from '~/services/useLogoutMutation';
import { useUserProfile } from '~/services/useUserProfileQuery';

import { FirstRunScreen } from './FirstRunScreen';
import {
  TeacherSetupStep,
  ReferralSourceStep,
  ClassSetupStep,
  FERPAStep,
  VoiceNameInstructionsStep,
  RosterStep,
} from './steps';
import SparklesSvg from './sparkles.svg';

const log = createLogger('SetupPage');

type SetupStep =
  | 'welcome'
  | 'tutorial'
  | 'privacy'
  | 'teacher'
  | 'referral'
  | 'class'
  | 'ferpa'
  | 'voice-instructions'
  | 'roster';

const STEP_ORDER: SetupStep[] = [
  'welcome',
  'tutorial',
  'privacy',
  'teacher',
  'referral',
  'class',
  'ferpa',
  'voice-instructions',
  'roster',
];

export function SetupPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isSetupSubmitted = sessionStore((state) => state.isSetupSubmitted);
  const setIsSetupSubmitted = sessionStore((state) => state.setIsSetupSubmitted);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLeftoversModalOpen, setIsLeftoversModalOpen] = useState(false);

  const { isPending: isUserPending } = useUserProfile();
  const { mutate: setup } = useSetupMutation();
  const { mutate: logout } = useLogoutMutation();

  const {
    isTeacherDataValid,
    isReferralSourceValid,
    isClassDataValid,
    isRosterValid,
    referredToName,
    schoolName,
    location,
    teachingStyle,
    tosAgreedAt,
    referralSource,
    firstClassName,
    firstShortName,
    voiceNameItems,
    addNameInputValue,
    clear: clearSetupStore,
  } = setupStore();

  const currentStep = (searchParams.get('step') as SetupStep) || 'welcome';
  const currentStepIndex = STEP_ORDER.indexOf(currentStep);

  // Redirect to welcome step if no step is specified
  useEffect(() => {
    if (!searchParams.get('step')) {
      log.debug('No step specified, redirecting to welcome');
      navigate('/setup?step=welcome', { replace: true });
    }
  }, [searchParams, navigate]);

  const handleSetupSubmit = useCallback(() => {
    if (!(isTeacherDataValid() && isReferralSourceValid() && isClassDataValid() && isRosterValid())) {
      showMessage({
        message: 'Please complete all required fields',
        type: 'warning',
      });
      return;
    }

    const setupData = {
      referredToName,
      schoolName,
      location,
      teachingStyle,
      tosAgreedAt,
      referralSource,
      firstClassName,
      firstShortName,
      studentVoiceNames: voiceNameItems.map((item) => item.name),
    };

    setIsSubmitting(true);

    setup(
      { setupData },
      {
        onSuccess: () => {
          setIsSubmitting(false);
          clearSetupStore();
          setIsSetupSubmitted(true);
          trackEvent('setup');
          trackConversion('setup');
          navigate('/welcome', { replace: true });
        },
        onError: (error) => {
          setIsSubmitting(false);
          showMessage({
            message: `Setup failed: ${error?.message || 'Unknown error'}`,
            type: 'danger',
          });
        },
      },
    );
  }, [
    isTeacherDataValid,
    isReferralSourceValid,
    isClassDataValid,
    isRosterValid,
    referredToName,
    schoolName,
    location,
    teachingStyle,
    tosAgreedAt,
    referralSource,
    firstClassName,
    firstShortName,
    voiceNameItems,
    setup,
    clearSetupStore,
    setIsSetupSubmitted,
    navigate,
  ]);

  const isNextDisabled = useCallback(() => {
    switch (currentStep) {
      case 'teacher':
        return !isTeacherDataValid();
      case 'referral':
        return !isReferralSourceValid();
      case 'class':
        return !isClassDataValid();
      case 'roster':
        return !isRosterValid();
      default:
        return false;
    }
  }, [currentStep, isTeacherDataValid, isReferralSourceValid, isClassDataValid, isRosterValid]);

  const handleNext = useCallback(() => {
    // Check for leftovers only on roster step (which is the final step)
    if (currentStep === 'roster' && addNameInputValue.trim().length > 0) {
      setIsLeftoversModalOpen(true);
      return;
    }

    const nextIndex = currentStepIndex + 1;
    const nextStep = nextIndex < STEP_ORDER.length ? (STEP_ORDER[nextIndex] as SetupStep) : null;
    if (nextStep) {
      navigate(`/setup?step=${nextStep}`);
    } else {
      // Final step, submit
      handleSetupSubmit();
    }
  }, [currentStep, currentStepIndex, addNameInputValue, navigate, handleSetupSubmit]);

  const handleBack = useCallback(() => {
    const prevIndex = currentStepIndex - 1;
    const prevStep = prevIndex >= 0 ? (STEP_ORDER[prevIndex] as SetupStep) : null;
    if (prevStep) {
      navigate(`/setup?step=${prevStep}`);
    } else {
      // First step, logout
      clearSetupStore();
      logout();
    }
  }, [currentStepIndex, navigate, clearSetupStore, logout]);

  if (isSetupSubmitted || isUserPending) {
    log.info('Setup page is already submitted or user profile is loading, showing loading spinner');
    return <Loading />;
  }

  // Render training screens (welcome, tutorial, privacy)
  const isTrainingScreen = currentStep === 'welcome' || currentStep === 'tutorial' || currentStep === 'privacy';
  if (isTrainingScreen) {
    return <FirstRunScreen currentStep={currentStep} />;
  }

  // Render setup steps with progress indicator
  const setupSteps = ['teacher', 'referral', 'class', 'ferpa', 'voice-instructions', 'roster'];
  const setupStepIndex = setupSteps.indexOf(currentStep) + 1; // 1-6

  return (
    <View style={styles.container}>
      <Image source={{ uri: SparklesSvg }} style={styles.background} resizeMode="contain" />
      <View style={styles.logoContainer}>
        <Logo height={38} width={38} />
      </View>
      <View style={styles.contentContainer}>
        {setupStepIndex > 0 && (
          <View style={styles.progressContainer}>
            <SetupProgressIndicator currentStep={setupStepIndex} completedSteps={setupStepIndex - 1} />
          </View>
        )}

        {currentStep === 'roster' ? (
          // Roster step gets the full scroll area
          <View style={styles.rosterStepContainer}>
            <RosterStep />
          </View>
        ) : (
          // Other steps use ScrollView
          <ScrollView
            style={styles.scrollView}
            contentContainerStyle={styles.scrollContent}
            showsVerticalScrollIndicator={true}>
            {currentStep === 'teacher' && <TeacherSetupStep />}
            {currentStep === 'referral' && <ReferralSourceStep />}
            {currentStep === 'class' && <ClassSetupStep />}
            {currentStep === 'ferpa' && <FERPAStep />}
            {currentStep === 'voice-instructions' && <VoiceNameInstructionsStep />}
          </ScrollView>
        )}

        <View style={styles.buttonContainer}>
          <Button name="Back" onPress={handleBack} telemetry-label="setup-back" />
          <Button
            name={'Next'}
            onPress={handleNext}
            disabled={isNextDisabled() || isSubmitting}
            loading={isSubmitting}
            telemetry-label="setup-next"
            variant="primary"
          />
        </View>
      </View>
      <LeftoversModal
        isOpen={isLeftoversModalOpen}
        onClose={() => setIsLeftoversModalOpen(false)}
        onContinue={() => {
          setIsLeftoversModalOpen(false);
          // Since roster is the last step, proceed directly to submission
          handleSetupSubmit();
        }}
      />
    </View>
  );
}

const MAX_WIDTH = 450;

const styles = StyleSheet.create({
  container: {
    height: '100%',
    width: '100%',
    position: 'relative',
  },
  background: {
    position: 'absolute',
    zIndex: 0,
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
  },
  logoContainer: {
    position: 'absolute',
    top: theme.spacing.xxlarge,
    left: theme.spacing.xxlarge,
    zIndex: 1,
  },
  contentContainer: {
    flex: 1,
    flexDirection: 'column',
    padding: theme.spacing.xxlarge,
    gap: theme.spacing.xlarge,
  },
  progressContainer: {
    alignSelf: 'center',
    maxWidth: MAX_WIDTH,
    width: '100%',
  },
  scrollView: {
    flex: 1,
  },
  scrollContent: {
    alignSelf: 'center',
    gap: theme.spacing.xxlarge,
    maxWidth: MAX_WIDTH,
    width: '100%',
    paddingBottom: theme.spacing.xxlarge,
  },
  rosterStepContainer: {
    flex: 1,
    alignSelf: 'center',
    maxWidth: MAX_WIDTH,
    width: '100%',
    minHeight: 0, // Critical: allows flex child to shrink below content size
  },
  buttonContainer: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignSelf: 'center',
    width: '100%',
  },
});
